import React, { useState } from "react";
import { Switch } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudUpload } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { useDispatch } from "react-redux";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import PremiumFeatureModal from "../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import {
  clearRegisterData,
  registerAccountStore,
} from "../../../store/slices/accountSlice";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";

export default function ProfileSettingsSection({
  isUploading,
  profilePicture,
  onDragDropUpload,
  onDragOver,
  onFileUpload,
  enabled,
  onTogglePublicProfile,
  hasSubscriptionAccess = true,
  hasUsedFreeTrial = false,
}) {
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;

  const openSubscription = () => {
    const updatedData = {
      userId: user.id,
      role: "athlete",
      email: user.email,
      registered: true,
      eventId: 0,
    };
    dispatch(clearRegisterData());
    dispatch(registerAccountStore(updatedData));
    localStorage.setItem("registeredAccount", JSON.stringify(updatedData));
    localStorage.removeItem("subscriptionDetails");
    setTimeout(() => {
      window.location.href = "/register/subscription";
    }, 2000);
  };

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handleUploadClick = (e) => {
    if (!hasSubscriptionAccess) {
      e.preventDefault();
      setOpenPremiumFeatureModal(true);
    }
  };

  const handleTogglePublicProfile = (value) => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    onTogglePublicProfile(value);
  };

  return (
    <div className="bg-white shadow-lg p-4 rounded-lg border-gray-300">
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Profile settings are locked. Start a free trial or subscribe now to update your profile."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div className="w-full">
        <h2 className="text-xl mb-6 uppercase ">Profile picture </h2>
        <div className="grid grid-cols-3 gap-4">
          <div
            className="flex items-center justify-items-end"
            onDrop={onDragDropUpload}
            onDragOver={onDragOver}
          >
            {isUploading ? (
              <div className="border border-blue-700 border-dashed rounded-lg p-4 bg-[#E6E3E3] flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : profilePicture ? (
              <div className="relative border border-blue-700 border-dashed rounded-lg cursor-pointer px-4 py-4 bg-[#E6E3E3]">
                <img
                  src={profilePicture.fileUrl}
                  alt="Profile"
                  className="h-[148px] w-[148px] object-cover rounded-lg"
                />
                <label
                  htmlFor="Upload"
                  className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity"
                  onClick={handleUploadClick}
                >
                  <FontAwesomeIcon
                    icon={faCloudUpload}
                    className="text-white h-6"
                  />
                  <p className="mb-2 text-sm text-white text-center">
                    <span className="font-normal">
                      Drag and drop or click to upload image
                    </span>
                  </p>
                </label>
                <input
                  id="Upload"
                  type="file"
                  className="hidden"
                  onChange={onFileUpload}
                />
              </div>
            ) : (
              <label
                htmlFor="Upload"
                className="border border-blue-700 border-dashed rounded-lg cursor-pointer px-4 py-4 bg-[#E6E3E3]"
                onClick={handleUploadClick}
              >
                <div className="flex flex-col items-center">
                  <FontAwesomeIcon
                    icon={faCloudUpload}
                    className="text-gray-600 h-6"
                  />
                  <p className="mb-2 text-sm text-gray-500 text-center">
                    <span className="font-normal">
                      Drag & Drop Image Or Click to Upload
                    </span>
                  </p>
                </div>
                <input
                  id="Upload"
                  type="file"
                  className="hidden"
                  onChange={onFileUpload}
                />
              </label>
            )}
          </div>
          <div className="text-base text-gray-700 text-right">
            Public Profile
          </div>
          <div>
            <Switch
              checked={!enabled}
              onChange={handleTogglePublicProfile}
              className="group relative flex h-7 w-14 cursor-pointer rounded-full bg-blue-700 p-1 transition-colors duration-200 ease-in-out focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[checked]:bg-gray-300"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-white ring-0 shadow-lg transition duration-200 ease-in-out group-data-[checked]:translate-x-7"
              />
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
}
